import React, { useEffect, useRef } from 'react';
import { BRAND_COLORS } from './constants';

// Version 2.2: 3D Pipeline Network Background (Enhanced)
export const Pipeline3DBackground: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Configuration state
        let config = {
            rotationSpeed: 0.5,
            pipelineCount: 12,
            flowSpeed: 1,
            scale: 100,
            globalOpacity: 80,
            globalScale: 100
        };

        // Listen for configuration changes
        const handleConfigChange = (event: CustomEvent) => {
            const newConfig = event.detail;
            config = {
                rotationSpeed: newConfig.v22RotationSpeed || config.rotationSpeed,
                pipelineCount: newConfig.v22PipelineCount || config.pipelineCount,
                flowSpeed: newConfig.v22FlowSpeed || config.flowSpeed,
                scale: newConfig.v22Scale || config.scale,
                globalOpacity: newConfig.globalOpacity || config.globalOpacity,
                globalScale: newConfig.globalScale || config.globalScale
            };
            // Reinitialize pipelines if count changed
            if (pipelines.length !== config.pipelineCount) {
                initializePipelines();
            }
        };

        window.addEventListener('backgroundConfigChange', handleConfigChange as EventListener);

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        // 3D Pipeline system with perspective projection
        interface Point3D {
            x: number;
            y: number;
            z: number;
        }

        interface Pipeline3D {
            start: Point3D;
            end: Point3D;
            segments: Point3D[];
            color: string;
            thickness: number;
            animationOffset: number;
            id: string;
        }

        let pipelines: Pipeline3D[] = [];
        const cameraDistance = 300;
        let rotationX = 0.1; // Reduced for subtlety
        let rotationY = 0;

        // Project 3D point to 2D screen
        const project3D = (point: Point3D) => {
            // Apply rotation
            const cosX = Math.cos(rotationX);
            const sinX = Math.sin(rotationX);
            const cosY = Math.cos(rotationY);
            const sinY = Math.sin(rotationY);

            let x = point.x;
            let y = point.y * cosX - point.z * sinX;
            let z = point.y * sinX + point.z * cosX;

            const newX = x * cosY + z * sinY;
            const newZ = -x * sinY + z * cosY;
            x = newX;
            z = newZ;

            // Perspective projection
            const scale = cameraDistance / (cameraDistance + z);
            return {
                x: canvas.width / 2 + x * scale,
                y: canvas.height / 2 + y * scale,
                scale
            };
        };

        // Generate random 3D pipeline with collision avoidance
        const createPipeline = (): Pipeline3D => {
            const start: Point3D = {
                x: (Math.random() - 0.5) * 400,
                y: (Math.random() - 0.5) * 300,
                z: (Math.random() - 0.5) * 200
            };

            const segments: Point3D[] = [start];
            let current = { ...start };

            // Create L-shaped segments (like real pipelines) with collision avoidance
            for (let i = 0; i < 3 + Math.floor(Math.random() * 4); i++) {
                let attempts = 0;
                let next: Point3D;

                do {
                    const direction = Math.floor(Math.random() * 3); // x, y, or z direction
                    const distance = 30 + Math.random() * 80; // Smaller segments for less collision

                    next = { ...current };
                    if (direction === 0) {
                        next.x += (Math.random() - 0.5) * distance;
                    } else if (direction === 1) {
                        next.y += (Math.random() - 0.5) * distance;
                    } else {
                        next.z += (Math.random() - 0.5) * distance;
                    }

                    attempts++;
                } while (attempts < 5 && isPointTooClose(next, segments));

                segments.push(next);
                current = next;
            }

            return {
                start,
                end: current,
                segments,
                color: Math.random() < 0.5 ? BRAND_COLORS.primary : BRAND_COLORS.secondary,
                thickness: 1.5 + Math.random() * 2.5, // Thinner for subtlety
                animationOffset: Math.random() * Math.PI * 2,
                id: Math.random().toString(36).substr(2, 9)
            };
        };

        // Check if point is too close to existing segments
        const isPointTooClose = (point: Point3D, existingSegments: Point3D[]): boolean => {
            return existingSegments.some(seg => {
                const dx = point.x - seg.x;
                const dy = point.y - seg.y;
                const dz = point.z - seg.z;
                const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
                return distance < 40; // Minimum distance between segments
            });
        };

        // Initialize pipelines
        const initializePipelines = () => {
            pipelines = [];
            for (let i = 0; i < config.pipelineCount; i++) {
                pipelines.push(createPipeline());
            }
        };

        initializePipelines();

        let animationFrameId: number;
        let time = 0;

        const animate = () => {
            time += 0.01 * config.flowSpeed;
            rotationY += 0.002 * config.rotationSpeed; // Much slower, configurable rotation

            // Subtle background - not black, use brand colors with very low opacity
            ctx.fillStyle = BRAND_COLORS.background;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Add very subtle gradient overlay
            const gradient = ctx.createRadialGradient(
                canvas.width / 2, canvas.height / 2, 0,
                canvas.width / 2, canvas.height / 2, Math.max(canvas.width, canvas.height)
            );
            gradient.addColorStop(0, `${BRAND_COLORS.primary}05`);
            gradient.addColorStop(1, `${BRAND_COLORS.secondary}03`);
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Sort pipelines by depth for proper rendering
            const pipelineDepths = pipelines.map((pipeline, index) => {
                const avgZ = pipeline.segments.reduce((sum, seg) => sum + seg.z, 0) / pipeline.segments.length;
                return { pipeline, index, avgZ };
            }).sort((a, b) => a.avgZ - b.avgZ);

            // Draw pipelines
            pipelineDepths.forEach(({ pipeline }) => {
                const projectedSegments = pipeline.segments.map(seg => project3D(seg));

                // Draw pipeline segments
                for (let i = 0; i < projectedSegments.length - 1; i++) {
                    const start = projectedSegments[i];
                    const end = projectedSegments[i + 1];

                    // Depth-based opacity and thickness
                    const avgScale = (start.scale + end.scale) / 2;
                    const baseOpacity = Math.max(0.1, Math.min(0.6, avgScale)); // Reduced max opacity
                    const finalOpacity = baseOpacity * (config.globalOpacity / 100);
                    const totalScale = (config.scale * config.globalScale) / 10000;

                    ctx.strokeStyle = `${pipeline.color}${Math.floor(finalOpacity * 255).toString(16).padStart(2, '0')}`;
                    ctx.lineWidth = pipeline.thickness * avgScale * totalScale;
                    ctx.lineCap = 'round';

                    ctx.beginPath();
                    ctx.moveTo(start.x, start.y);
                    ctx.lineTo(end.x, end.y);
                    ctx.stroke();

                    // Add flowing energy effect (more subtle)
                    const flowProgress = (time + pipeline.animationOffset) % 1;
                    const flowX = start.x + (end.x - start.x) * flowProgress;
                    const flowY = start.y + (end.y - start.y) * flowProgress;

                    if (avgScale > 0.3) { // Only show on visible segments
                        const energyOpacity = finalOpacity * 0.8; // More subtle energy
                        ctx.fillStyle = `${pipeline.color}${Math.floor(energyOpacity * 255).toString(16).padStart(2, '0')}`;
                        ctx.beginPath();
                        ctx.arc(flowX, flowY, 2 * avgScale * totalScale, 0, Math.PI * 2);
                        ctx.fill();
                    }
                }

                // Draw joints/connectors (more subtle)
                projectedSegments.forEach(point => {
                    if (point.scale > 0.2) {
                        const jointOpacity = Math.max(0.1, point.scale * 0.4) * (config.globalOpacity / 100);
                        const totalScale = (config.scale * config.globalScale) / 10000;
                        ctx.fillStyle = `${pipeline.color}${Math.floor(jointOpacity * 255).toString(16).padStart(2, '0')}`;
                        ctx.beginPath();
                        ctx.arc(point.x, point.y, 3 * point.scale * totalScale, 0, Math.PI * 2);
                        ctx.fill();
                    }
                });
            });

            // Occasionally add new pipelines (less frequent)
            if (Math.random() < 0.002 && pipelines.length < config.pipelineCount + 5) {
                pipelines.push(createPipeline());
            }

            // Remove old pipelines occasionally
            if (Math.random() < 0.001 && pipelines.length > config.pipelineCount) {
                pipelines.splice(Math.floor(Math.random() * pipelines.length), 1);
            }

            animationFrameId = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('backgroundConfigChange', handleConfigChange as EventListener);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 pointer-events-none z-0"
        />
    );
};
